import { Body, Box } from '@qred/components-library';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DocumentUploadSection } from '~/components/shared/DocumentUpload/DocumentUploadSection';
import Stack from '~/components/shared/Layout/Stack';
import { ModalFooter } from '~/components/shared/Modal/Modal';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useDocumentUpload } from '~/hooks/useDocumentUpload';
import useTranslate from '~/hooks/useTranslate';
import { IFile } from '~/interfaces/shared';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import useSubmit from '../../Steps/useSubmit';
import { fileConfigByDocumentType, DocumentType } from '~/constants/fileUpload';
import { CountryCode } from '~/enums';

export const UploadDocumentsModalBody = () => {
  const t = useTranslate();
  const onSubmit = useSubmit();
  const dispatch = useAppDispatch();

  const market = useSelector((state: RootState) => state.intl.market);
  const { apiStatus } = useSelector(
    (state: RootState) => state.loanApplication
  );

  const documentType =
    market === CountryCode.NL ? DocumentType.MT940 : DocumentType.Other;
  const [localFiles, setLocalFiles] = useState<IFile[]>([]);
  const { documentsApiStatus, uploadDocuments } = useDocumentUpload({
    documentType,
  });

  const onSubmitButtonClick = async () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'click_upload_documents_and_submit_button',
      })
    );

    const filePaths = await uploadDocuments(localFiles);
    if (filePaths) {
      dispatch(
        updateForm({
          bankFilesUploaded: true,
          bankFilesPaths: filePaths,
        })
      );
    }
    onSubmit();
  };

  const onSkipAndSubmitButtonClick = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'click_skip_upload_documents_and_submit_application_button',
      })
    );

    onSubmit();
  };

  return (
    <Stack spacing="md">
      <Body size="lg">{t('Onboarding.UploadFilesModalDescription')}</Body>
      <DocumentUploadSection
        fileConfig={fileConfigByDocumentType[documentType]}
        hasApiError={documentsApiStatus === ApiStatus.Failed}
        onFilesChange={setLocalFiles}
      />
      <ModalFooter fixedHeaderAndFooter filledBackground>
        <Box>
          <NextButton
            action={
              localFiles.length
                ? onSubmitButtonClick
                : onSkipAndSubmitButtonClick
            }
            label={
              localFiles.length
                ? 'Onboarding.Submit'
                : 'Onboarding.SkipAndSubmit'
            }
            isLoading={
              documentsApiStatus === ApiStatus.Started ||
              apiStatus.patch === ApiStatus.Started
            }
          />
        </Box>
      </ModalFooter>
    </Stack>
  );
};
